/**
 * TenantLifecycleModals.jsx
 *
 * Houses the three little dialogs that drive tenant lifecycle:
 *   1. Put-on-notice        (active → notice, with a planned checkout date)
 *   2. Edit checkout date   (change the planned/actual exit date any time)
 *   3. Mark exited          (notice → exited, stamping the actual checkout)
 *
 * All three talk to the backend directly; parent passes `onSaved` to refresh.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Calendar, LogOut, CheckCircle2, Clock } from 'lucide-react';
import { toast } from '../../hooks/use-toast';
import { emit, EVT } from '../../services/dataSyncBus';
import { track } from '../../services/analytics';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtDate = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};
const todayStr = () => fmtDate(new Date());
const addDays = (iso, days) => {
  const base = iso ? new Date(iso) : new Date();
  base.setDate(base.getDate() + days);
  return fmtDate(base);
};

// ----------------- Cancel Notice -----------------
export const CancelNoticeModal = ({ open, onClose, tenant, token, onSaved }) => {
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    setSaving(true);
    try {
      await axios.delete(`${API}/tenants/${tenant.id}/notice`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      emit(EVT.TENANT, { action: 'notice_cancelled', id: tenant.id });
      emit(EVT.ROOM,   { action: 'tenant_lifecycle', room_id: tenant?.room_id });
      track('tenant_notice_cancelled', { tenant_id: tenant.id });
      toast({
        title: 'Notice cancelled',
        description: `${tenant.name} is back to active. Room stays occupied.`,
      });
      onSaved?.();
      onClose();
    } catch (err) {
      toast({ title: 'Failed', description: err.response?.data?.detail || 'Could not cancel notice', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="cancel-notice-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CheckCircle2 size={18} className="text-green-600" /> Cancel notice period
          </DialogTitle>
          <DialogDescription>
            Reverting <b>{tenant?.name}</b> back to <b>active</b>. The planned checkout date
            <b> {tenant?.planned_check_out_date && ` (${new Date(tenant.planned_check_out_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })})`}</b> will be cleared.
            Use this if the tenant changed their mind and decided to stay.
          </DialogDescription>
        </DialogHeader>
        <div className="flex justify-end gap-2 pt-2 border-t">
          <Button type="button" variant="outline" onClick={onClose}>Keep on notice</Button>
          <Button
            onClick={submit}
            disabled={saving}
            className="bg-green-600 hover:bg-green-700 text-white"
            data-testid="cancel-notice-submit"
          >
            {saving ? 'Cancelling…' : 'Cancel notice — keep tenant active'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// ----------------- Put on Notice -----------------
export const PutOnNoticeModal = ({ open, onClose, tenant, token, onSaved }) => {
  const [noticeStart, setNoticeStart] = useState('');
  const [checkoutDate, setCheckoutDate] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    const today = todayStr();
    setNoticeStart(today);
    // Default checkout = today + tenant.notice_period_days (set at booking time;
    // falls back to 30 if missing). Editable below with quick chips.
    const days = tenant?.notice_period_days || 30;
    setCheckoutDate(addDays(today, days));
  }, [open, tenant]);

  const submit = async (e) => {
    e.preventDefault();
    if (!checkoutDate) {
      return toast({ title: 'Checkout date required', variant: 'destructive' });
    }
    if (new Date(checkoutDate) < new Date(noticeStart)) {
      return toast({ title: 'Invalid dates', description: 'Checkout must be on or after notice start date', variant: 'destructive' });
    }
    setSaving(true);
    try {
      await axios.patch(
        `${API}/tenants/${tenant.id}/notice`,
        { notice_start_date: noticeStart, check_out_date: checkoutDate },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      emit(EVT.TENANT, { action: 'put_on_notice', id: tenant.id });
      emit(EVT.ROOM,   { action: 'tenant_lifecycle', room_id: tenant?.room_id });
      track('tenant_put_on_notice', { tenant_id: tenant.id, notice_days: tenant?.notice_period_days || 30 });
      toast({
        title: 'Tenant on notice',
        description: `${tenant.name} · checkout ${new Date(checkoutDate).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}`,
      });
      onSaved?.();
      onClose();
    } catch (err) {
      toast({ title: 'Failed', description: err.response?.data?.detail || 'Could not put on notice', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="put-on-notice-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Clock size={18} className="text-orange-600" /> Put on notice period
          </DialogTitle>
          <DialogDescription>
            Notice period from booking T&amp;C: <b>{tenant?.notice_period_days || 30} days</b>. Adjust planned checkout if the tenant is leaving earlier or later than that.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label className="flex items-center gap-1"><Calendar size={12} /> Notice start date</Label>
            <Input
              type="date"
              value={noticeStart}
              max={todayStr()}
              onChange={(e) => setNoticeStart(e.target.value)}
              data-testid="notice-start-date"
              required
            />
            <p className="text-[10px] text-gray-500 mt-0.5">Usually today.</p>
          </div>
          <div>
            <Label className="flex items-center gap-1"><Calendar size={12} /> Planned checkout date *</Label>
            <Input
              type="date"
              value={checkoutDate}
              min={noticeStart}
              onChange={(e) => setCheckoutDate(e.target.value)}
              data-testid="notice-checkout-date"
              required
              className="font-semibold"
            />
            <div className="flex gap-1.5 mt-1.5">
              {[
                { label: '15 days', days: 15 },
                { label: '30 days', days: 30 },
                { label: '45 days', days: 45 },
              ].map(p => (
                <button
                  type="button"
                  key={p.days}
                  onClick={() => setCheckoutDate(addDays(noticeStart, p.days))}
                  className="text-[10px] px-2 py-1 rounded-full bg-gray-100 hover:bg-orange-100 text-gray-700"
                >
                  {p.label}
                </button>
              ))}
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-2 border-t">
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button
              type="submit"
              disabled={saving}
              className="bg-orange-600 hover:bg-orange-700 text-white"
              data-testid="notice-submit"
            >
              {saving ? 'Saving…' : 'Put on notice'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// ----------------- Edit Checkout Date -----------------
export const EditCheckoutDateModal = ({ open, onClose, tenant, token, onSaved }) => {
  const [checkoutDate, setCheckoutDate] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setCheckoutDate(tenant?.check_out_date || tenant?.planned_check_out_date || addDays(todayStr(), 30));
  }, [open, tenant]);

  const submit = async (e) => {
    e.preventDefault();
    if (!checkoutDate) {
      return toast({ title: 'Date required', variant: 'destructive' });
    }
    setSaving(true);
    try {
      await axios.patch(
        `${API}/tenants/${tenant.id}`,
        { check_out_date: checkoutDate },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      emit(EVT.TENANT, { action: 'checkout_date_updated', id: tenant.id });
      toast({ title: 'Checkout date updated' });
      onSaved?.();
      onClose();
    } catch (err) {
      toast({ title: 'Failed', description: err.response?.data?.detail || 'Save failed', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="edit-checkout-date-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Calendar size={18} className="text-blue-600" /> Edit checkout date
          </DialogTitle>
          <DialogDescription>
            Tenants sometimes leave earlier or later than planned. Update the date and room availability will stay in sync.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label>Check-in date</Label>
            <Input
              type="date"
              value={tenant?.check_in_date || ''}
              disabled
              className="bg-gray-50 text-gray-500"
            />
          </div>
          <div>
            <Label>Checkout date *</Label>
            <Input
              type="date"
              value={checkoutDate}
              min={tenant?.check_in_date}
              onChange={(e) => setCheckoutDate(e.target.value)}
              data-testid="edit-checkout-input"
              required
              className="font-semibold"
            />
          </div>
          <div className="flex justify-end gap-2 pt-2 border-t">
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button
              type="submit"
              disabled={saving}
              className="bg-blue-600 hover:bg-blue-700 text-white"
              data-testid="edit-checkout-submit"
            >
              {saving ? 'Saving…' : 'Save'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// ----------------- Mark Exited -----------------
export const MarkExitedModal = ({ open, onClose, tenant, token, onSaved }) => {
  const [checkoutDate, setCheckoutDate] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setCheckoutDate(tenant?.check_out_date || tenant?.planned_check_out_date || todayStr());
  }, [open, tenant]);

  const submit = async (e) => {
    e.preventDefault();
    if (!checkoutDate) {
      return toast({ title: 'Date required', variant: 'destructive' });
    }
    setSaving(true);
    try {
      // Uses the dedicated /checkout endpoint which syncs room occupancy
      await axios.patch(
        `${API}/tenants/${tenant.id}/checkout?checkout_date=${checkoutDate}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      emit(EVT.TENANT, { action: 'exited', id: tenant.id });
      emit(EVT.ROOM,   { action: 'tenant_exited', room_id: tenant?.room_id });
      track('tenant_exited', { tenant_id: tenant.id });
      toast({ title: 'Tenant exited', description: `Room freed. Checkout: ${new Date(checkoutDate).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}` });
      onSaved?.();
      onClose();
    } catch (err) {
      toast({ title: 'Failed', description: err.response?.data?.detail || 'Save failed', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="mark-exited-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <LogOut size={18} className="text-red-600" /> Mark tenant as exited
          </DialogTitle>
          <DialogDescription>
            This frees the bed immediately. If the tenant left earlier or later than planned, set the actual checkout date below.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label>Actual checkout date *</Label>
            <Input
              type="date"
              value={checkoutDate}
              min={tenant?.check_in_date}
              onChange={(e) => setCheckoutDate(e.target.value)}
              data-testid="exit-checkout-input"
              required
              className="font-semibold"
            />
            {tenant?.planned_check_out_date && tenant.planned_check_out_date !== checkoutDate && (
              <p className="text-[11px] text-orange-700 mt-1">
                ⚠️ Planned was {new Date(tenant.planned_check_out_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })} — you're saving a different date.
              </p>
            )}
          </div>
          <div className="flex justify-end gap-2 pt-2 border-t">
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button
              type="submit"
              disabled={saving}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="exit-submit"
            >
              {saving ? 'Saving…' : <><CheckCircle2 size={14} className="mr-1" /> Confirm exit</>}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
